/**
 * The still follows the open ingredient row in a UTERIFIC "what's inside" band.
 *
 * Each botanical row carries `data-ut-ingredient` and each photograph carries
 * `data-ut-still`, matched by block id. Opening a row brings its still forward.
 *
 * The section renders correctly without this file: the first still is visible by
 * default, which is what a single static image would have given. The script marks the
 * section `is-ready` before it touches anything, and only then does the stylesheet hand
 * control of which still shows over to `.is-active`.
 *
 * Load this with type="module".
 */

/**
 * Exclusive accordion for browsers that do not support the `name` attribute on <details>.
 * Where it is supported the browser already keeps one row open and this does nothing, so
 * the two never fight over the same click.
 */
const SUPPORTS_EXCLUSIVE = 'name' in document.createElement('details');

function keepOneOpen(section) {
  if (SUPPORTS_EXCLUSIVE) return;

  const group = Array.from(section.querySelectorAll('details[name]'));
  if (group.length < 2) return;

  group.forEach((row) => {
    row.addEventListener('toggle', () => {
      if (!row.open) return;
      for (const other of group) {
        if (other !== row && other.name === row.name) other.open = false;
      }
    });
  });

  // More than one may be open on arrival; the last one wins, matching the native rule.
  const open = group.filter((row) => row.open);
  for (const row of open.slice(0, -1)) row.open = false;
}

function init(section) {
  keepOneOpen(section);

  const stills = Array.from(section.querySelectorAll('[data-ut-still]'));
  const rows = Array.from(section.querySelectorAll('[data-ut-ingredient]'));
  if (stills.length === 0 || rows.length === 0) return;

  // Rows and stills are paired by block id, not position, because a botanical can be
  // listed before it has a photograph. A row with no still leaves the current one up
  // rather than blanking the column.
  const stillFor = (row) => stills.find((still) => still.dataset.utStill === row.dataset.utIngredient);
  const show = (still) => {
    if (!still) return;
    stills.forEach((other) => other.classList.toggle('is-active', other === still));
  };

  rows.forEach((row) => {
    // `toggle` fires on open and on close; only an opening row takes the frame, so
    // closing the last open row leaves its still in place rather than blanking the column.
    row.addEventListener('toggle', () => {
      if (row.open) show(stillFor(row));
    });
  });

  section.classList.add('is-ready');
  const openRow = rows.find((row) => row.open);
  show((openRow && stillFor(openRow)) || stills[0]);
}

function initAll(root = document) {
  root.querySelectorAll('[data-ut-inside]').forEach(init);
}

initAll();
document.addEventListener('shopify:section:load', (event) => initAll(event.target));
